<script setup lang="ts">
    import {
        DefaultChatTransport,
        lastAssistantMessageIsCompleteWithToolCalls,
    } from 'ai'
    import { computed, provide, toRefs, watch, ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { generateId } from '@ai-sdk/ui-utils'
    import { Chat } from '@ai-sdk/vue'
    import { useLocalStorage } from '@vueuse/core'
    import type {
        Chat as Conversation,
        AgentInterface,
        AgentModel,
        ChatMessageActions,
        MessageFeedback,
        RevisedAnswer,
        UIChatMessage,
    } from 'models'
    import { logger } from 'utils/src/logger'
    import { collectDeviceContext } from 'utils/src/device-context'
    import PkAvatar from './PkAvatar.vue'
    import PkChatbotViewChat from './PkChatbotViewChat.vue'
    import PkChatbotViewConversations from './PkChatbotViewConversations.vue'
    import PkChatbotViewProfile from './PkChatbotViewProfile.vue'
    import { chatbotContextKey } from './constants'
    import { getContrast, getTextPart } from './utils'

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    const props = defineProps<{
        api: string
        agentId: string
        agentType?: 'reasoning' | 'chat'
        userId?: string
        name?: string
        model?: string
        agentModel?: AgentModel
        agentInterface?: AgentInterface
        actions?: ChatMessageActions[]
        revisedAnswers?: RevisedAnswer[]
        headers?: Record<string, string>
    }>()
    const emit = defineEmits<{
        'message-update': [message: UIChatMessage]
        'show-info': [message: UIChatMessage]
        revise: [message: UIChatMessage]
    }>()

    const {
        agentId,
        agentType,
        name,
        model,
        agentModel,
        agentInterface,
        revisedAnswers,
        headers,
    } = toRefs(props)
    const chatId = defineModel<string | undefined>('chatId', {
        type: String,
        default: undefined,
    })

    const isReasoningAgent = computed(() => {
        return agentType.value === 'reasoning'
    })

    const actions = computed(() => {
        if (!isReasoningAgent.value) {
            return props.actions
        }
        return props.actions?.filter(
            (action) => !['upvote', 'downvote', 'feedback'].includes(action),
        )
    })

    const storageId = useLocalStorage<string | null>(
        computed(() => `${agentId.value}-chatId`),
        null,
    )

    // When the agent changes, ensure a stable chat ID exists for it
    watch(
        agentId,
        () => {
            if (!storageId.value) {
                storageId.value = generateId()
            }
        },
        { immediate: true },
    )

    const localChatId = computed({
        get: () => chatId.value || storageId.value!,
        set: (value) => {
            storageId.value = value
            chatId.value = value
        },
    })

    const inputMessagePlaceholder = computed(
        () =>
            agentInterface.value?.messagePlaceholder ||
            $t('placeholder.askAnything'),
    )

    const input = ref('')
    const chat = computed(
        () =>
            new Chat<UIChatMessage>({
                id: localChatId.value,
                transport: new DefaultChatTransport({
                    api: `${props.api}/agents/${agentId.value}/${isReasoningAgent.value ? 'reasoning-chat' : 'chat'}`,
                    prepareSendMessagesRequest: ({ messages, id }) => {
                        return {
                            body: {
                                id,
                                messages: [messages[messages.length - 1]],
                                model: model.value,
                                prompt: agentModel.value?.prompt,
                                maxOutputTokens: agentModel.value?.maxOutputTokens,
                                temperature: agentModel.value?.temperature,
                                presencePenalty:
                                    agentModel.value?.presencePenalty,
                                frequencyPenalty:
                                    agentModel.value?.frequencyPenalty,
                                searchMode: agentModel.value?.searchMode,
                                limit: agentModel.value?.limit,
                                deviceContext: collectDeviceContext(),
                            },
                        }
                    },
                    credentials: 'include',
                    headers: props.headers,
                }),
                onToolCall: (toolCall) => {
                    logger.log('Tool call:', toolCall)
                },
                sendAutomaticallyWhen:
                    lastAssistantMessageIsCompleteWithToolCalls,
                onError: (error) => {
                    logger.error(error)
                },
            }),
    )

    // initialize conversations
    const conversations = ref<Conversation[]>([])
    const loadConversations = async (agentId: string) => {
        if (isReasoningAgent.value) {
            // reasoning agent chats are not persisted, so no need to load
            conversations.value = []
            return
        }
        const response = await fetch(
            `${props.api}/agents/${agentId}/chats?sort=-updatedAt&page[size]=100&filter[userId]=${props.userId}`,
            {
                credentials: 'include',
                headers: props.headers,
            },
        )
        if (response.ok) {
            conversations.value = (await response.json()) as Conversation[]
            return
        }
        conversations.value = []
    }
    const loadChat = async (agentId: string, chatId: string) => {
        if (isReasoningAgent.value) {
            // reasoning agent chats are not persisted, so no need to load
            chat.value.messages = []
            return
        }
        const response = await fetch(
            `${props.api}/agents/${agentId}/chats/${chatId}`,
            {
                credentials: 'include',
                headers: props.headers,
            },
        )
        if (response.ok && response.status !== 204) {
            chat.value.messages = (
                (await response.json()) as Chat<UIChatMessage>
            ).messages
            return
        }
        chat.value.messages = []
    }

    // messages with welcome
    const welcomeMessage = computed<UIChatMessage>(() => ({
        id: generateId(),
        role: 'assistant',
        parts: [
            {
                type: 'text',
                text:
                    agentInterface.value?.initialMessage ||
                    $t('message.defaultAgentWelcome'),
            },
        ],
    }))

    const messages = computed(() => {
        if (isReasoningAgent.value) {
            return chat.value.messages
        }
        if (chat.value.messages?.[0]?.role !== 'assistant') {
            return [welcomeMessage.value, ...chat.value.messages]
        }
        return chat.value.messages
    })

    // internal tab-based navigation (no vue-router dependency)
    type ChatbotView = 'chat' | 'conversations' | 'profile'
    const activeView = ref<ChatbotView>('chat')
    const navigate = (view: ChatbotView) => {
        activeView.value = view
    }

    // send message
    const handleSubmit = async () => {
        const text = input.value.trim()
        if (isReasoningAgent.value && messages.value.length > 0) {
            await startNewChat()
        }
        chat.value.sendMessage({ text })
        input.value = ''
    }

    // stop message generation
    const stopGeneration = async () => {
        return await chat.value.stop()
    }

    // start a new chat by clearing messages and generating a new chat ID
    const startNewChat = async () => {
        await stopGeneration()
        localChatId.value = generateId()
        chat.value.clearError()
        navigate('chat')
        return localChatId.value
    }

    // add tool output to chat (used by client-side tools like showMultipleChoice)
    // sendAutomaticallyWhen will re-submit once all tool outputs are available
    const addToolOutput = (params: {
        tool: string
        toolCallId: string
        output: unknown
    }) => {
        chat.value.addToolOutput(params)
    }

    // message feedbacks for the current chat
    const messageFeedbacks = ref<MessageFeedback[]>([])
    const loadMessageFeedbacks = async (chatId: string) => {
        const response = await fetch(
            `${props.api}/message-feedbacks?filter[chatId]=${chatId}&page[size]=200`,
            {
                credentials: 'include',
                headers: props.headers,
            },
        )
        if (response.ok) {
            messageFeedbacks.value =
                (await response.json()) as MessageFeedback[]
            return
        }
        messageFeedbacks.value = []
    }

    const upsertLocalFeedback = (feedback: MessageFeedback) => {
        const idx = messageFeedbacks.value.findIndex(
            (f) => f.messageId === feedback.messageId,
        )
        if (idx >= 0) {
            messageFeedbacks.value[idx] = feedback
        } else {
            messageFeedbacks.value.push(feedback)
        }
    }

    /**
     * Extracts the user question and assistant answer text for the given
     * assistant message, looking up the preceding user turn in messages.
     */
    const extractFeedbackContext = (message: UIChatMessage) => {
        const msgList = messages.value
        const idx = msgList.findIndex((m) => m.id === message.id)
        const assistantMessage = getTextPart(message) ?? ''
        const prevUser = msgList
            .slice(0, idx)
            .reverse()
            .find((m) => m.role === 'user')
        const userQuestion = getTextPart(prevUser ?? message) ?? ''
        return { userQuestion, assistantMessage }
    }

    const submitFeedback = async (
        message: UIChatMessage,
        vote: 'upvote' | 'downvote' | undefined,
        userQuestion: string,
        assistantMessage: string,
        comment?: string,
    ) => {
        const response = await fetch(`${props.api}/message-feedbacks`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...props.headers,
            },
            credentials: 'include',
            body: JSON.stringify({
                messageId: message.id,
                chatId: localChatId.value,
                agentId: agentId.value,
                vote,
                userQuestion,
                assistantMessage,
                comment,
            }),
        })
        if (!response.ok) {
            throw new Error('Failed to submit feedback')
        }
        const feedback = (await response.json()) as MessageFeedback
        upsertLocalFeedback(feedback)
    }

    const onUpvote = async (message: UIChatMessage) => {
        const { userQuestion, assistantMessage } =
            extractFeedbackContext(message)
        await submitFeedback(message, 'upvote', userQuestion, assistantMessage)
    }
    const onDownvote = async (message: UIChatMessage) => {
        const { userQuestion, assistantMessage } =
            extractFeedbackContext(message)
        await submitFeedback(
            message,
            'downvote',
            userQuestion,
            assistantMessage,
        )
    }

    const feedbackDialogMessage = ref<UIChatMessage | undefined>()
    const isFeedbackSubmitting = ref(false)
    const isFeedbackSubmitted = ref(false)
    const feedbackSubmitError = ref<string | undefined>()
    const onFeedback = (message: UIChatMessage) => {
        isFeedbackSubmitted.value = false
        feedbackSubmitError.value = undefined
        feedbackDialogMessage.value = message
    }
    const onFeedbackSubmit = async (comment: string) => {
        if (!feedbackDialogMessage.value) {
            return
        }
        try {
            isFeedbackSubmitting.value = true
            isFeedbackSubmitted.value = false
            feedbackSubmitError.value = undefined
            const { userQuestion, assistantMessage } = extractFeedbackContext(
                feedbackDialogMessage.value,
            )
            await submitFeedback(
                feedbackDialogMessage.value,
                undefined,
                userQuestion,
                assistantMessage,
                comment,
            )
            isFeedbackSubmitted.value = true
            await loadMessageFeedbacks(localChatId.value)
        } catch {
            feedbackSubmitError.value = $t('message.feedbackSubmitError')
        } finally {
            isFeedbackSubmitting.value = false
        }
    }

    // submit lead from contact form
    const isLeadSubmitted = ref(false)
    const isLoadingSubmitLead = ref(false)
    const submitLeadError = ref<string>()
    const onLeadSubmit = async (data: Record<string, unknown>) => {
        try {
            isLoadingSubmitLead.value = true
            isLeadSubmitted.value = false
            submitLeadError.value = undefined
            const response = await fetch(`${props.api}/leads`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...props.headers,
                },
                body: JSON.stringify({
                    agentId: agentId.value,
                    chatId: localChatId.value,
                    ...data,
                }),
                credentials: 'include',
            })
            if (!response.ok) {
                throw new Error('Failed to submit lead')
            }
            isLeadSubmitted.value = true
        } catch {
            submitLeadError.value = $t('message.leadSubmitError')
        } finally {
            isLoadingSubmitLead.value = false
        }
    }

    const regenerate = async (
        options: Parameters<typeof chat.value.regenerate>[0],
    ) => {
        if (chat.value.status !== 'ready') {
            await stopGeneration()
        }
        await chat.value.regenerate(options)
    }

    // provide chat context to view components
    provide(chatbotContextKey, {
        api: props.api,
        agentId,
        name,
        agentInterface,
        actions,
        revisedAnswers,
        conversations,
        headers,
        messages,
        inputMessagePlaceholder,
        chat,
        input,
        localChatId,
        handleSubmit,
        stopGeneration,
        addToolOutput,
        startNewChat,
        isLeadSubmitted,
        isLoadingSubmitLead,
        submitLeadError,
        onLeadSubmit,
        messageFeedbacks,
        feedbackDialogMessage,
        isFeedbackSubmitting,
        isFeedbackSubmitted,
        feedbackSubmitError,
        onUpvote,
        onDownvote,
        onFeedback,
        onFeedbackSubmit,
        emit,
        navigate,
        regenerate,
    })

    // ui
    const useColorsForAgentHeader = computed(
        () => agentInterface.value?.useColorsForAgentHeader,
    )
    const themeClass = computed(() => {
        if (!agentInterface.value?.theme) return ''
        return `theme--${agentInterface.value.theme}`
    })
    const mainColor = computed(() => agentInterface.value?.mainColor)
    const contrastColor = computed(() => getContrast(mainColor.value))
    const title = computed(() => {
        if (activeView.value === 'conversations') {
            return $t('label.conversations')
        }
        if (activeView.value === 'profile') {
            return $t('label.profile')
        }
        return props.name
    })

    // state
    const isReady = computed(() => chat.value.status === 'ready')
    const activeMessage = computed(() => {
        return messages.value?.[messages.value.length - 1]
    })

    // expose
    defineExpose({
        startNewChat,
        regenerate,
        stopGeneration,
    })

    // load chat, conversations and feedbacks on agent or chat change
    watch(
        [agentId, localChatId],
        async ([newAgentId, newChatid]) => {
            await Promise.all([
                loadChat(newAgentId, newChatid),
                loadConversations(newAgentId),
                loadMessageFeedbacks(newChatid),
            ])
        },
        {
            immediate: true,
        },
    )
    // update initial message
    watch(
        () => agentInterface.value?.initialMessage,
        () => {
            if (
                chat.value.messages.length === 1 &&
                chat.value.messages[0].role === 'assistant'
            ) {
                chat.value.messages = []
            }
        },
    )
    // load conversations when navigating to conversations view
    watch(activeView, (newView) => {
        if (newView === 'conversations') {
            loadConversations(agentId.value)
        }
    })
    // emit message updates for up-to-date context in parent components
    watch(
        activeMessage,
        (message) => {
            if (message) {
                emit('message-update', message)
            }
        },
        {
            deep: true,
            immediate: true,
        },
    )
</script>

<template>
    <div
        class="flex flex-col relative h-full bg-surface theme"
        :class="themeClass">
        <!-- #region title -->
        <div
            class="sticky z-1 bg-surface-1 flex justify-between gap-sm top-0 p-sm border-b border-surface-4"
            :style="
                useColorsForAgentHeader
                    ? {
                          backgroundColor: mainColor,
                      }
                    : undefined
            ">
            <div class="flex items-center gap-sm min-w-0">
                <template v-if="activeView !== 'chat'">
                    <VvButton
                        v-bind="{
                            modifiers: useColorsForAgentHeader
                                ? `action-quiet-${contrastColor}`
                                : 'action-quiet',
                            icon: 'ri:arrow-left-s-line',
                            title: $t('action.goBack'),
                        }"
                        @click="navigate('chat')" />
                    <VvIcon
                        name="ri:history-line"
                        class="text-18"
                        :style="
                            useColorsForAgentHeader
                                ? {
                                      color: contrastColor,
                                  }
                                : undefined
                        " />
                </template>
                <PkAvatar
                    v-else
                    modifiers="surface ring shrink-0"
                    class="w-24 h-24"
                    :name
                    :img-src="agentInterface?.logo" />
                <strong
                    class="text-16 font-bold truncate"
                    :title="title"
                    :style="
                        useColorsForAgentHeader
                            ? {
                                  color: contrastColor,
                              }
                            : undefined
                    ">
                    {{ title }}
                </strong>
            </div>
            <VvButtonGroup class="shrink-0" modifiers="compact">
                <VvDropdown
                    v-bind="{
                        placement: 'bottom-end',
                        modifiers: 'menu',
                        flip: true,
                        offset: 3,
                    }">
                    <VvButton
                        v-bind="{
                            modifiers: useColorsForAgentHeader
                                ? `action-quiet-${contrastColor}`
                                : 'action-quiet',
                            icon: 'ri:more-2-fill',
                            disabled: !isReady,
                            title: $t('action.moreActions'),
                        }" />
                    <template #items>
                        <VvDropdownAction @click="startNewChat()">
                            <VvIcon name="ri:edit-box-line" />
                            {{ $t('action.startNewChat') }}
                        </VvDropdownAction>
                        <VvDropdownAction
                            v-if="!isReasoningAgent"
                            @click="navigate('conversations')">
                            <VvIcon name="ri:history-line" />
                            {{ $t('action.viewRecentChats') }}
                        </VvDropdownAction>
                    </template>
                </VvDropdown>
                <slot
                    name="actions"
                    v-bind="{
                        mainColor,
                        useColorsForAgentHeader,
                        contrastColor,
                        startNewChat,
                        stopGeneration,
                    }" />
            </VvButtonGroup>
        </div>
        <!-- #endregion -->

        <div v-if="isReasoningAgent">
            <div class="p-md border-b border-surface-4">
                <VvAlert modifiers="callout">
                    <div class="flex items-center gap-4 mb-4">
                        <VvIcon name="ri:alert-line" class="w-16 h-16" />
                        <strong class="font-semibold text-14">
                            {{ $t('message.warning') }}
                        </strong>
                    </div>
                    <p class="text-12 text-word-3">
                        {{ $t('message.reasoningAgent') }}
                    </p>
                </VvAlert>
            </div>
        </div>

        <!-- #region views -->
        <PkChatbotViewChat v-if="activeView === 'chat'">
            <template v-for="(_, slotName) in $slots" #[slotName]="slotData"
                ><slot :name="slotName" v-bind="slotData"
            /></template>
        </PkChatbotViewChat>
        <PkChatbotViewConversations v-else-if="activeView === 'conversations'">
            <template v-for="(_, slotName) in $slots" #[slotName]="slotData"
                ><slot :name="slotName" v-bind="slotData"
            /></template>
        </PkChatbotViewConversations>
        <PkChatbotViewProfile v-else>
            <template v-for="(_, slotName) in $slots" #[slotName]="slotData"
                ><slot :name="slotName" v-bind="slotData"
            /></template>
        </PkChatbotViewProfile>
        <!-- #endregion -->

        <!-- #region footer -->
        <div
            v-if="agentInterface?.footerMessage"
            class="p-md border-t border-surface-4 text-center text-12 text-word-2">
            {{ agentInterface?.footerMessage }}
        </div>
        <!-- #endregion -->
    </div>
</template>
